/**
 * FRONT-895 "Create a project from...", generalized beyond chat by FRONT-1122:
 * mutations for the two backend endpoints (`src/work/plan-routes.ts`) —
 * `useExtractPlan` fires the read-only LLM pass on dialog open, `useCommitPlan`
 * fires the deterministic create on confirm. Both take a `PlanSource` (chat /
 * file / document / cron-run), not just a chat session id.
 *
 * The raw request functions (`extractPlan`, `commitPlan`) are exported
 * separately from their hooks so the wire shape is unit-testable without a
 * QueryClient — mirrors `createWorkTask` etc. in `use-work-task-mutations.ts`.
 *
 * `commitPlan` goes through the shared `api.post` helper (FRONT-895 review
 * fix) rather than a bare `api.raw` fetch — `api.raw` bypassed `request()`'s
 * 401 silent-refresh-then-retry (`api-client.ts`), so a mid-session token
 * expiry surfaced as a hard failure instead of a transparent retry. The
 * commit route's 409 body (`{reason:"duplicate_project", existing}`) has no
 * `error` string field, so it can't be read off `ApiError.message`; instead
 * `ApiError` now also carries the parsed JSON body (`.body`), which this
 * function reads to throw the typed `DuplicateProjectError` so the dialog can
 * offer "Use existing project instead" instead of a dead-end toast.
 *
 * FRONT-1122 follow-up: the extracted draft (and the commit it produces) now
 * also covers `people` (mirrors the Work detail page's "Important people",
 * `container-detail-mapping.ts`'s `ImportantPerson`) and `context` (mirrors
 * that page's "Context" — labelled facts + free-text notes,
 * `ContextFact`). Both are optional on the commit request — "nothing ticked"
 * means the field is left off the body entirely, not sent as an empty array —
 * and the commit response reports per-person/context outcomes additively so
 * the dialog can say plainly when part of a commit didn't land.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '~/lib/api-client';
import { chatKeys, graphKeys, workBoardKeys, workTaskKeys } from '~/lib/query-keys';

// ---- extract -------------------------------------------------------------

export interface PlanDraftTask {
  title: string;
  dueDate: string | null;
  /** FRONT-2423: the extract pass's own concrete detail for this task (who,
   *  what, numbers, decisions), or null when the title already says
   *  everything. Optional (not just nullable) so every existing fixture/
   *  test built before this field existed still typechecks — the real
   *  `/api/work/plan/extract` response always sends it. */
  description?: string | null;
}

/**
 * FRONT-1122 defect fix: client-side mirrors of the server's own caps
 * (`src/work/plan-routes.ts`'s `MAX_COMMIT_PEOPLE`/`PERSON_NAME_MAX`/
 * `PERSON_ROLE_MAX`/`MAX_COMMIT_CONTEXT_FACTS`/`CONTEXT_LABEL_MAX`/
 * `CONTEXT_VALUE_MAX`/`CONTEXT_NOTES_MAX`), kept in exact numeric sync so a
 * form the user finished filling out never 400s the whole commit after the
 * fact — enforced at the dialog's inputs via `maxLength` and row-count gates
 * (`create-plan-dialog.tsx`). `PERSON_ROLE_MAX` deliberately matches the
 * existing add-a-person route's own cap, not an independent number, so a
 * role this dialog shows is always one the commit accepts.
 */
export const MAX_COMMIT_PEOPLE = 20;
export const PERSON_NAME_MAX = 200;
export const PERSON_ROLE_MAX = 80;
export const MAX_COMMIT_CONTEXT_FACTS = 50;
export const CONTEXT_LABEL_MAX = 120;
export const CONTEXT_VALUE_MAX = 2000;
export const CONTEXT_NOTES_MAX = 4000;

/** A proposed "important person" — max `MAX_COMMIT_PEOPLE` per draft, name
 *  ≤`PERSON_NAME_MAX` chars, role ≤`PERSON_ROLE_MAX` chars (server-enforced,
 *  now also capped client-side at the same numbers — see
 *  create-plan-dialog.tsx). */
export interface PlanDraftPerson {
  name: string;
  role?: string;
}

/** One labelled "context" detail — max `MAX_COMMIT_CONTEXT_FACTS` per draft,
 *  label ≤`CONTEXT_LABEL_MAX` chars, value ≤`CONTEXT_VALUE_MAX` chars. */
export interface PlanDraftContextFact {
  label: string;
  value: string;
}

/** The proposed "Context" section — labelled facts plus a free-text summary
 *  (notes ≤4000 chars). An empty draft comes back as `{facts:[], notes:""}`. */
export interface PlanDraftContext {
  facts: PlanDraftContextFact[];
  notes: string;
}

export type PlanDraftProject =
  | { mode: 'new'; name: string }
  | { mode: 'existing'; entityId: string; name?: string };

export interface PlanDraft {
  project: PlanDraftProject;
  tasks: PlanDraftTask[];
  people: PlanDraftPerson[];
  context: PlanDraftContext;
}

export interface ExtractPlanResponse {
  draft: PlanDraft;
}

/**
 * FRONT-1122: what an extract/commit pass reads from — a chat transcript, an
 * uploaded file, a memory document, or a cron-run briefing. Mirrors the
 * backend's `PlanSource` discriminated union exactly (`src/work/plan-routes.ts`);
 * every call to either endpoint sends one of these as `source` now, replacing
 * the old bare `sessionId` (the backend still accepts the old shape, but the
 * frontend no longer sends it).
 */
export type PlanSource =
  | { kind: 'chat'; sessionId: string }
  | { kind: 'file'; fileId: string }
  | { kind: 'document'; filename: string }
  | { kind: 'cron-run'; runId: number }
  | { kind: 'text'; text: string };

/** `source.kind:'text'`'s own text can run to `TRANSCRIPT_CHAR_BUDGET`
 *  (24,000) characters (Copilot review on #1213: the key used to carry the
 *  whole paste verbatim, bloating every dependency-array diff and any tool
 *  that logs it). A prefix alone would risk two DIFFERENT pastes that happen
 *  to open with the same boilerplate (e.g. two Claude Project instructions
 *  both starting with the same header) comparing equal, so this keys off
 *  length plus a short prefix AND suffix — cheap, bounded, and collision-safe
 *  for anything this key actually needs (a `useEffect` dependency comparison,
 *  never displayed or sent to the server). */
const TEXT_KEY_EDGE_LEN = 40;

/**
 * A stable primitive key for a `PlanSource` — used as a `useEffect`
 * dependency (see `create-plan-dialog.tsx`) so the dialog's "fire the extract
 * pass on open" effect keys off the source's actual identity rather than
 * object reference: call sites build `source` as a fresh inline object
 * literal (e.g. `source={{kind:'chat', sessionId}}`), which would otherwise
 * change on every parent re-render and re-fire the extract pass.
 */
export function planSourceKey(source: PlanSource): string {
  switch (source.kind) {
    case 'chat':
      return `chat:${source.sessionId}`;
    case 'file':
      return `file:${source.fileId}`;
    case 'document':
      return `document:${source.filename}`;
    case 'cron-run':
      return `cron-run:${source.runId}`;
    case 'text': {
      const t = source.text;
      if (t.length <= TEXT_KEY_EDGE_LEN * 2) return `text:${t}`;
      return `text:${t.length}:${t.slice(0, TEXT_KEY_EDGE_LEN)}:${t.slice(-TEXT_KEY_EDGE_LEN)}`;
    }
  }
}

/** Always 200 per the backend contract — a total extract failure degrades
 *  server-side to an empty manual draft rather than an error response. */
export function extractPlan(source: PlanSource): Promise<ExtractPlanResponse> {
  return api.post<ExtractPlanResponse>('/api/work/plan/extract', { source });
}

export function useExtractPlan() {
  return useMutation({ mutationFn: extractPlan });
}

// ---- commit ---------------------------------------------------------------

/** The dialog's current project selection — "draft" half of the edited state. */
export type PlanProjectDraft =
  | { mode: 'new'; name: string }
  | { mode: 'existing'; entityId: string; name: string };

/** One task row as the dialog edits it — "edits" half of the edited state. */
export interface PlanTaskEdit {
  title: string;
  dueDate: string | null;
  included: boolean;
  /** FRONT-2423: carried unchanged from the extract draft into the commit
   *  payload — this dialog never edits it, only shows it. See
   *  `PlanDraftTask.description` for why this is optional rather than
   *  nullable-required. */
  description?: string | null;
}

/** One person row as the dialog edits it — mirrors `PlanTaskEdit`'s shape. */
export interface PlanPersonEdit {
  name: string;
  role: string;
  included: boolean;
}

/** One context-fact row as the dialog edits it — mirrors `PlanTaskEdit`'s
 *  shape. The free-text notes are edited as a single string, not a row list,
 *  so they're passed to `resolvePlanCommitPayload` separately. */
export interface PlanFactEdit {
  label: string;
  value: string;
  included: boolean;
}

export interface CommitPlanBody {
  project: { mode: 'new'; name: string } | { mode: 'existing'; entityId: string };
  tasks: Array<{ title: string; dueDate: string | null; description: string | null }>;
  /** Omitted entirely when no ticked row has a non-empty name — "nothing
   *  ticked" means the field is absent, never an empty array. */
  people?: Array<{ name: string; role?: string }>;
  /** Omitted entirely when there's nothing to send — no ticked fact with a
   *  non-empty label AND no notes text. */
  context?: { facts: Array<{ label: string; value: string }>; notes: string };
}

/**
 * Pure: turns the dialog's current project selection, task rows, person
 * rows, fact rows, and notes text into the exact request body for
 * `/api/work/plan/commit` — excluded rows are dropped, text fields are
 * trimmed (a row that trims to an empty required field — a task title, a
 * person name, a fact label — is dropped too, never sent blank), and only
 * `entityId` (never the display name) is sent for an existing-mode project.
 * `source` is NOT part of this — it's a dialog prop, not part of the edited
 * draft/task/people/context state, and is attached by the caller when
 * building the full `CommitPlanRequestBody`.
 *
 * `peopleEdits`/`factEdits`/`notes` default to empty so every existing call
 * site (and test) that only deals in tasks keeps working unchanged.
 */
export function resolvePlanCommitPayload(
  draft: PlanProjectDraft,
  edits: PlanTaskEdit[],
  peopleEdits: PlanPersonEdit[] = [],
  factEdits: PlanFactEdit[] = [],
  notes: string = '',
): CommitPlanBody {
  const project: CommitPlanBody['project'] =
    draft.mode === 'existing'
      ? { mode: 'existing', entityId: draft.entityId }
      : { mode: 'new', name: draft.name.trim() };

  const tasks = edits
    .filter((t) => t.included)
    .map((t) => ({ title: t.title.trim(), dueDate: t.dueDate, description: t.description ?? null }))
    .filter((t) => t.title.length > 0);

  const people = peopleEdits
    .filter((p) => p.included)
    .map((p) => ({ name: p.name.trim(), role: p.role.trim() || undefined }))
    .filter((p) => p.name.length > 0);

  const facts = factEdits
    .filter((f) => f.included)
    .map((f) => ({ label: f.label.trim(), value: f.value.trim() }))
    .filter((f) => f.label.length > 0);

  const trimmedNotes = notes.trim();

  const body: CommitPlanBody = { project, tasks };
  if (people.length > 0) {
    body.people = people;
  }
  if (facts.length > 0 || trimmedNotes.length > 0) {
    body.context = { facts, notes: trimmedNotes };
  }
  return body;
}

export interface CommitPlanRequestBody extends CommitPlanBody {
  source: PlanSource;
}

export interface CommitPlanResult {
  project: { id: string; name: string; type: string; existed: boolean };
  tasks: Array<{ title: string; ok: boolean; id?: string }>;
  createdCount: number;
  /**
   * FRONT-1122 defect fix: always present on the response (empty array when
   * nothing was sent) — the backend contract settled on always emitting
   * `people`, so the dialog no longer needs to tell "nothing to report"
   * apart from "sent, all failed" by presence/absence. `roleSkipped` marks a
   * person who was already on the project under a different role — the
   * typed role was deliberately left alone (never-clobber), not written.
   */
  people: Array<{
    name: string;
    ok: boolean;
    id?: string;
    existed?: boolean;
    linked?: boolean;
    roleSkipped?: boolean;
  }>;
  /**
   * Always present. `factsSkippedExisting` counts a ticked detail that
   * wasn't written because that label is already on the project (the
   * never-clobber rule working as designed — the user must still be told,
   * since they may have deliberately edited the value). `factsSkippedLimit`
   * counts one skipped because the project already has 50 details.
   * `notesSkipped` says why the free-text notes weren't appended:
   * `"duplicate"` (already there) or `"limit"` (would have gone over 4000
   * characters).
   */
  context: {
    factsWritten: number;
    factsSkippedExisting: number;
    factsSkippedLimit: number;
    notesWritten: boolean;
    notesSkipped?: 'duplicate' | 'limit';
    ok: boolean;
  };
}

/** Thrown by `commitPlan` on a 409 `duplicate_project` response — carries the
 *  ACTIVE project the new name collided with, so the dialog can offer
 *  "Use existing project instead" (flip to existing mode + re-commit) rather
 *  than a dead-end error.
 *
 * FRONT-1265 code-review: `matchedVia`/`requestedName` tell apart a real
 * active-name collision from a hit on a retired name an earlier merge left
 * behind as an also-known-as alias — in the alias case, what the user typed
 * (`requestedName`) and the existing project's actual name (`existing.name`)
 * are two different strings, so the message must name both instead of
 * implying the user's own input already exists verbatim. */
export class DuplicateProjectError extends Error {
  existing: { id: string; name: string };
  matchedVia: 'name' | 'alias';
  requestedName: string;
  constructor(existing: { id: string; name: string }, matchedVia: 'name' | 'alias', requestedName: string) {
    super(
      matchedVia === 'alias'
        ? `"${requestedName}" is an also-known-as name for "${existing.name}" — that's an existing project`
        : `A project named "${existing.name}" already exists`,
    );
    this.name = 'DuplicateProjectError';
    this.existing = existing;
    this.matchedVia = matchedVia;
    this.requestedName = requestedName;
  }
}

export async function commitPlan(body: CommitPlanRequestBody): Promise<CommitPlanResult> {
  try {
    return await api.post<CommitPlanResult>('/api/work/plan/commit', body);
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      const parsed = (err.body ?? {}) as {
        reason?: unknown;
        existing?: { id?: unknown; name?: unknown };
        matchedVia?: unknown;
        requestedName?: unknown;
      };
      const existing = parsed.existing;
      if (
        parsed.reason === 'duplicate_project' &&
        existing &&
        typeof existing.id === 'string' &&
        typeof existing.name === 'string'
      ) {
        const matchedVia = parsed.matchedVia === 'alias' ? 'alias' : 'name';
        const requestedName = typeof parsed.requestedName === 'string' ? parsed.requestedName : existing.name;
        throw new DuplicateProjectError({ id: existing.id, name: existing.name }, matchedVia, requestedName);
      }
    }
    throw err;
  }
}

/**
 * On success, invalidate exactly: `workBoardKeys.all` (the board's card grid
 * and stat strip — the recurring stale-board trap, FRONT-1003), `graphKeys.all`
 * (the new/attached-to project entity), and — only when the commit attached to
 * an EXISTING container (`result.project.existed`) — `workTaskKeys.byContainer`
 * for that container, so its already-open task disclosure refreshes too. A
 * freshly created project has no prior task-list cache entry to invalidate.
 *
 * FRONT-2563: a commit from a chat files that chat under the project when it
 * had none, so a chat source also refreshes that chat's own scope (the
 * "About: <name>" chip) and `threadsRoot()` (the project's Chats panel and
 * the Chats overview) — the same pair `useSetChatScope` refreshes. The
 * server's `chat-scope-changed` event does this too; this covers a tab whose
 * event stream is down.
 */
export function useCommitPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: commitPlan,
    onSuccess: (result, body) => {
      qc.invalidateQueries({ queryKey: workBoardKeys.all });
      qc.invalidateQueries({ queryKey: graphKeys.all });
      if (result.project.existed) {
        qc.invalidateQueries({ queryKey: workTaskKeys.byContainer(result.project.id) });
      }
      if (body.source.kind === 'chat') {
        qc.invalidateQueries({ queryKey: chatKeys.scope(body.source.sessionId) });
        qc.invalidateQueries({ queryKey: chatKeys.threadsRoot() });
      }
    },
  });
}
